import { isNil } from 'lodash';

import {
  CustomerDraftResponse,
  CustomerEditResponse,
  DurationTypeEnum,
  ExtendedNamedAttachmentModel,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import {
  CustomerAdditionalInfoResponseModel,
  CustomerInsuranceRequirementsModel,
} from 'api/adminApiClient';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import { ClientInitialValues } from 'components/modals/ClientModal';
import { EditClientAdditionalInfoInitialValues } from 'components/modals/EditClientAdditionalInfoModal';
import { ClientCreationEnum } from 'components/modals/ClientModal/enum';
import { CustomerBulkImportDetailsModel } from '../../ClientBulkImportPage/ClientBulkImportTableSection/interface';
import { AgreementFileValue } from 'components/modals/ClientModal/ServiceAgreement/AgreementUploader/AgreementUploader';

type CustomerBulkImportWithoutIdDetailsModel = Omit<
  CustomerBulkImportDetailsModel,
  'id'
>;

const mapCustomerGeneralInfoToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
) => {
  const getCorrectData = () => {
    switch (creationType) {
      case ClientCreationEnum.EditImport:
        return customerData as CustomerBulkImportWithoutIdDetailsModel;
      case ClientCreationEnum.EditDraft:
        return customerData as CustomerDraftResponse;
      case ClientCreationEnum.Edit:
      default:
        return (customerData as CustomerEditResponse)?.customerDocument;
    }
  };

  const { generalInfo } = getCorrectData();
  const myBasePayTeam = generalInfo?.myBasePayTeam;

  const customerGeneralInfoInitialValues = {
    companyName: generalInfo?.companyInfo?.companyName || '',
    state: !isNil(generalInfo?.companyInfo?.location?.stateId)
      ? generalInfo?.companyInfo?.location?.stateId
      : undefined,
    city: generalInfo?.companyInfo?.location?.city || '',
    country: !isNil(generalInfo?.companyInfo?.location?.countryId)
      ? generalInfo?.companyInfo?.location?.countryId
      : undefined,
    internationalStateName: !isNil(
      generalInfo?.companyInfo?.location?.internationalStateName,
    )
      ? generalInfo?.companyInfo?.location?.internationalStateName
      : undefined,
    internationalCityName: !isNil(
      generalInfo?.companyInfo?.location?.internationalCityName,
    )
      ? generalInfo?.companyInfo?.location?.internationalCityName
      : undefined,
    mainAddress: generalInfo?.companyInfo?.location?.address || '',
    zip: generalInfo?.companyInfo?.location?.zipCode || '',
    companyWebsite: generalInfo?.companyInfo?.companyWebSite || '',
    additionalInvoiceDBA: generalInfo?.companyInfo?.additionalInvoiceName || '',
    companyEmail: generalInfo?.companyInfo?.companyEmail || '',
    companyPhone: generalInfo?.companyInfo?.companyPhoneNumber || '',
    primaryContacts: !!generalInfo?.primaryContacts?.length
      ? generalInfo?.primaryContacts?.map((contact) => ({
          id: contact.contactId,
          firstName: contact?.firstName || '',
          lastName: contact?.lastName || '',
          title: contact?.title || '',
          email: contact?.email || '',
          phone: contact?.phoneNumber || '',
          preferredContactMethod: contact?.preferredContactMethod ?? [],
          contactFor: contact?.contactFor ?? [],
        }))
      : [
          {
            firstName: '',
            lastName: '',
            title: '',
            email: '',
            phone: '',
            preferredContactMethod: [],
            contactFor: undefined,
          },
        ],
    mbpClientSuccessPartnerUserId:
      myBasePayTeam?.mbpClientSuccessPartnerUserId || undefined,
    mbpBillingRepresentativeUserId:
      myBasePayTeam?.mbpBillingRepresentativeUserId || undefined,
    mspSalesRepresentativeUserId:
      myBasePayTeam?.mspSalesRepresentativeUserId || undefined,
    mbpCollectionsRepresentativeUserId:
      myBasePayTeam?.mbpCollectionsRepresentativeUserId || undefined,
    mspWorkforceSpecialistUserId:
      myBasePayTeam?.mspWorkforceSpecialistUserId || undefined,
  };

  if (
    creationType === ClientCreationEnum.Edit ||
    creationType === ClientCreationEnum.EditDraft
  ) {
    const { customerGroupId } = customerData;

    return {
      ...customerGeneralInfoInitialValues,
      customerGroupId: !isNil(customerGroupId) ? customerGroupId : undefined,
    };
  }

  if (creationType === ClientCreationEnum.EditImport) {
    const { customerGroupName } = customerData;

    return {
      ...customerGeneralInfoInitialValues,
      customerGroupName: !isNil(customerGroupName)
        ? customerGroupName
        : undefined,
    };
  }

  return customerGeneralInfoInitialValues;
};

const mapCustomerBillingInfoToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
) => {
  const getCorrectData = () => {
    switch (creationType) {
      case ClientCreationEnum.EditImport:
        return customerData as CustomerBulkImportWithoutIdDetailsModel;
      case ClientCreationEnum.EditDraft:
        return customerData as CustomerDraftResponse;
      case ClientCreationEnum.Edit:
      default:
        return (customerData as CustomerEditResponse)?.customerDocument;
    }
  };

  const { billingInfo, generalInfo } = getCorrectData();

  return {
    billingContacts: !!billingInfo.billingContacts?.length
      ? billingInfo.billingContacts
      : [
          {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
          },
        ],
    billingLocation: {
      locationSameAsMain: billingInfo?.isBillingSameAsMainAddress || false,
      country: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.countryId
        : billingInfo?.location?.countryId || undefined,
      internationalStateName: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.internationalStateName
        : billingInfo?.location?.internationalStateName || undefined,
      internationalCityName: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.internationalCityName
        : billingInfo?.location?.internationalCityName || undefined,
      state: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.stateId || undefined
        : billingInfo?.location?.stateId || undefined,
      city: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo?.companyInfo?.location?.city || ''
        : billingInfo?.location?.city || '',
      address: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo.companyInfo.location.address || ''
        : billingInfo?.location?.address || '',
      zip: billingInfo?.isBillingSameAsMainAddress
        ? generalInfo.companyInfo.location.zipCode || ''
        : billingInfo?.location?.zipCode || '',
    },
  };
};

const mapCustomerAdditionalInfoToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
) => {
  const getCorrectData = () => {
    switch (creationType) {
      case ClientCreationEnum.EditImport:
        return customerData as CustomerBulkImportWithoutIdDetailsModel;
      case ClientCreationEnum.EditDraft:
        return customerData as CustomerDraftResponse;
      case ClientCreationEnum.Edit:
      default:
        return (customerData as CustomerEditResponse)?.customerDocument;
    }
  };

  const { additionalInfo } = getCorrectData();

  return {
    clientSize: !isNil(additionalInfo?.potentialHeadcount)
      ? additionalInfo?.potentialHeadcount
      : undefined,
    industry: additionalInfo?.industryName || '',
    haveBusinessBefore: additionalInfo?.haveBusinessBefore || false,
    yearsInBusiness: !isNil(additionalInfo?.yearsInBusiness)
      ? additionalInfo?.yearsInBusiness
      : undefined,
    abilityToPay: !isNil(additionalInfo?.abilityToPay)
      ? additionalInfo?.abilityToPay
      : undefined,
    billRate: !isNil(additionalInfo?.averageBillRate)
      ? additionalInfo?.averageBillRate
      : null,
    duration: !isNil(additionalInfo?.estimatedValue)
      ? additionalInfo?.estimatedValue
      : null,
    durationUnit: additionalInfo?.estimatedType || DurationTypeEnum.Week,
  };
};

const mapCustomerServiceAgreementToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
) => {
  const isEditImport = creationType === ClientCreationEnum.EditImport;
  const getCorrectData = () => {
    switch (creationType) {
      case ClientCreationEnum.EditImport:
        return customerData as CustomerBulkImportWithoutIdDetailsModel;
      case ClientCreationEnum.EditDraft:
        return customerData as CustomerDraftResponse;
      case ClientCreationEnum.Edit:
      default:
        return (customerData as CustomerEditResponse)?.customerDocument;
    }
  };
  const { agreementDetailsInfo } = getCorrectData();

  return {
    serviceAgreement:
      agreementDetailsInfo?.agreementDetailsType ||
      ServiceAgreementTypeEnum.MyBasePayAgreement,
    clientAgreement:
      agreementDetailsInfo?.agreementDetailsType ===
        ServiceAgreementTypeEnum.ClientAgreement &&
      agreementDetailsInfo?.agreementAttachments
        ? agreementDetailsInfo?.agreementAttachments.map(
            (file: AgreementFileValue | ExtendedNamedAttachmentModel) => ({
              fileName: file.fileName || '',
              s3Key: isEditImport
                ? (file as AgreementFileValue).s3Key
                : (file as ExtendedNamedAttachmentModel).fileKey || '',
              createdDate: file?.createdDate || undefined,
            }),
          )
        : [],
    memberAgreement:
      agreementDetailsInfo?.agreementDetailsType ===
        ServiceAgreementTypeEnum.MemberAgreement &&
      agreementDetailsInfo?.agreementAttachments
        ? agreementDetailsInfo?.agreementAttachments.map(
            (file: AgreementFileValue | ExtendedNamedAttachmentModel) => ({
              fileName: file.fileName || '',
              s3Key: isEditImport
                ? (file as AgreementFileValue).s3Key
                : (file as ExtendedNamedAttachmentModel).fileKey || '',
              createdDate: file?.createdDate || undefined,
            }),
          )
        : [],
    myBasePayAgreement:
      agreementDetailsInfo?.agreementDetailsType ===
        ServiceAgreementTypeEnum.MyBasePayAgreement &&
      agreementDetailsInfo?.agreementAttachments
        ? agreementDetailsInfo?.agreementAttachments.map(
            (file: AgreementFileValue | ExtendedNamedAttachmentModel) => ({
              fileName: file.fileName || '',
              s3Key: isEditImport
                ? (file as AgreementFileValue).s3Key
                : (file as ExtendedNamedAttachmentModel).fileKey || '',
              createdDate: file?.createdDate || undefined,
            }),
          )
        : [],
    isAgreementNotAvailableConfirmed:
      agreementDetailsInfo?.agreementDetailsType ===
      ServiceAgreementTypeEnum.NotAvailable
        ? agreementDetailsInfo?.isAgreementNotAvailableConfirmed
        : undefined,
    contractSignerFirstName: agreementDetailsInfo?.contractSignerFirstName || '',
    contractSignerLastName: agreementDetailsInfo?.contractSignerLastName || '',
    contractSignerPhone: agreementDetailsInfo?.contractSignerPhone || '',
    contractSignerEmail: agreementDetailsInfo?.contractSignerEmail || '',
    isContractSignerCompleted: agreementDetailsInfo?.isContractSignerCompleted,
  };
};

type CustomerResponse = CustomerDraftResponse | CustomerEditResponse;
export const mapCustomerDetailsToInitialValues = (
  customerData: CustomerResponse | CustomerBulkImportWithoutIdDetailsModel,
  creationType: ClientCreationEnum,
): ClientInitialValues => {
  return {
    generalInfo: mapCustomerGeneralInfoToInitialValues(
      customerData,
      creationType,
    ),
    billingInfo: mapCustomerBillingInfoToInitialValues(
      customerData,
      creationType,
    ),
    serviceAgreement: mapCustomerServiceAgreementToInitialValues(
      customerData,
      creationType,
    ),
    additionalInfo: mapCustomerAdditionalInfoToInitialValues(
      customerData,
      creationType,
    ),
  };
};

export const mapCustomerAdditionalDetailsToInitialValues = (
  customerAdditionalData: CustomerAdditionalInfoResponseModel,
): EditClientAdditionalInfoInitialValues => {
  const ci = customerAdditionalData.contractInformation;
  const fi = customerAdditionalData.financialInformation;
  const ai = customerAdditionalData.assignmentInformation;
  const ins = ci?.insuranceRequirements;
  /** Legacy API payloads may still carry SOW fields on insurance before migration */
  type InsuranceWithLegacySow = CustomerInsuranceRequirementsModel & {
    sowRequired?: boolean;
    sowTemplateFileKey?: string;
    sowSpecialInstructions?: string;
  };
  const insLegacy = ins as InsuranceWithLegacySow | undefined;
  const cr = ci?.contractRequirements;
  const np = ci?.noticeParty;
  const fo = fi?.financialOverview;
  const ro = fi?.rateOverview;
  const msp = fi?.mspOrVmsOverview;
  const ar = ai?.assignmentRequirements;
  const sar = ai?.specialAssignmentRequirements;

  const noticeLoc = np?.location;
  const noticeCountryId = noticeLoc?.countryId;
  const useNoticeStatesDropdown =
    checkIsCountriesWithStatesDropdown(noticeCountryId);

  const toFormDateString = (d?: Date | string) => {
    if (!d) return null;
    if (typeof d === 'string') return d;
    return d.toISOString();
  };

  return {
    generalContractInfo: {
      requiresCustomCOI: !isNil(ins?.requiresCustomCOI)
        ? ins?.requiresCustomCOI
        : undefined,
      certificate: ins?.certificateOfInsuranceCreatedFileKey
        ? [ins.certificateOfInsuranceCreatedFileKey]
        : [],
      submittedTo: ins?.submittedTo || '',
      contractSpecialInstructions: cr?.contractSpecialInstructions || '',
      canContractBeAssigned: !isNil(cr?.canContractBeAssigned)
        ? cr?.canContractBeAssigned
        : undefined,
      useOfNameLogoPermitted: !isNil(cr?.useOfNameLogoPermitted)
        ? cr?.useOfNameLogoPermitted
        : undefined,
      customerAgreementType: cr?.customerAgreementType ?? null,
      governingLawJurisdiction: cr?.governingLawJurisdiction || '',
      indemnificationType: cr?.indemnificationType ?? null,
      limitationOfLiability: cr?.limitationOfLiability || '',
      rightToInspectOrAudit: !isNil(cr?.rightToInspectOrAudit)
        ? cr?.rightToInspectOrAudit
        : undefined,
      contractedMBPServiceLines: cr?.contractedMBPServiceLines ?? [],
      dataPrivacyRequirements: !isNil(cr?.dataPrivacyRequirements)
        ? cr?.dataPrivacyRequirements
        : undefined,
      dataPrivacyInstructions: cr?.dataPrivacyInstructions || '',
      contractRenewalTerm: cr?.contractRenewalTerm ?? null,
      contractEffectiveDate: toFormDateString(cr?.contractEffectiveDate),
      contractExpirationDate: toFormDateString(cr?.contractExpirationDate),
      noticePeriodInDays: !isNil(cr?.noticePeriodInDays)
        ? cr?.noticePeriodInDays
        : null,
      sowRequired: !isNil(cr?.sowRequired)
        ? cr?.sowRequired
        : !isNil(insLegacy?.sowRequired)
          ? insLegacy?.sowRequired
          : undefined,
      sowTemplate:
        cr?.sowTemplateFileKey || insLegacy?.sowTemplateFileKey
          ? [
              String(
                cr?.sowTemplateFileKey ?? insLegacy?.sowTemplateFileKey ?? '',
              ),
            ]
          : [],
      sowSpecialInstructions:
        cr?.sowSpecialInstructions ||
        insLegacy?.sowSpecialInstructions ||
        '',
      declinationReason: cr?.declinationReason || '',
      attentionParty: np?.attentionParty || '',
      noticePartyCountryId: noticeCountryId,
      noticePartyStateId: useNoticeStatesDropdown
        ? noticeLoc?.stateId
        : undefined,
      noticePartyCity: useNoticeStatesDropdown ? noticeLoc?.city || '' : '',
      noticePartyInternationalCityName: !useNoticeStatesDropdown
        ? noticeLoc?.internationalCityName || ''
        : '',
      noticePartyInternationalStateName: !useNoticeStatesDropdown
        ? noticeLoc?.internationalStateName || ''
        : '',
      noticePartyAddress: noticeLoc?.address || '',
      noticePartyZipCode: noticeLoc?.zipCode || '',
      noticePartyEmail: np?.email || '',
      noticePartyPhone: np?.phone || '',
      noticePartyFaxNumber: np?.faxNumber || '',
      noticeMethod: np?.noticeMethod || '',
    },
    financialInfo: {
      markupRate: ro?.fixedMarkupRate || '',
      dtBillRateMultiplier: !isNil(ro?.dtBillRateMultiplier)
        ? ro?.dtBillRateMultiplier
        : null,
      otRateMultiplier: ro?.otRateMultiplier || '',

      creditCheck: fo?.creditCheckScore || '',
      commissionType: fo?.fundingType ?? null,
      notes: fo?.financialSpecialInstructions || '',
      paymentTerms: fo?.paymentTerm ?? null,

      MSPFee:
        typeof msp?.mspFee === 'number' ? msp.mspFee : undefined,
      VMSFee:
        typeof msp?.vmsFee === 'number' ? msp.vmsFee : undefined,
      invoicingInfo: fo?.invoicingInformation || '',
      invoicingInstructions: fo?.specialInvoicingInstructions || '',
      invoicingCycle: fo?.invoicingCycle ?? null,
      payrollCycle: fo?.payrollCycle ?? null,
      url: msp?.url || '',
      providerName: msp?.mspOrVmsProviderName || '',
      isThirdWorkforce: !isNil(msp?.isThereMspOrVms)
        ? msp?.isThereMspOrVms
        : undefined,
      purchaseOrder: !isNil(fo?.purchaseOrder) ? fo?.purchaseOrder : undefined,
      purchaseOrderFileKey: fo?.purchaseOrderFileKey
        ? [fo.purchaseOrderFileKey]
        : [],
      purchaseOrderInstructions: fo?.purchaseOrderInstructions || '',
      customerCostingMethodType: fo?.customerCostingMethodType ?? null,
      contractorConversionFees: !isNil(fo?.contractorConversionFees)
        ? fo?.contractorConversionFees
        : undefined,
      customerCreditNotes: fo?.customerCreditNotes || '',
      dunsNumber: fo?.dunsNumber || '',
      invoiceCutOffPeriod: fo?.invoiceCutOffPeriod || '',
      invoiceDisputePeriod: fo?.invoiceDisputePeriod || '',
      paymentMethod: fo?.paymentMethod ?? null,
      payorName: fo?.payorName || '',
    },
    assignmentRequirements: {
      contractorsPermitted: !isNil(
        ar?.isIndependentContractorsOrSubcontractors,
      )
        ? ar?.isIndependentContractorsOrSubcontractors
        : undefined,
      preScreeningRequired: !isNil(ar?.isPreScreeningRequired)
        ? ar?.isPreScreeningRequired
        : undefined,
      preScreeningBilledTo: !isNil(ar?.preScreeningBilledTo)
        ? ar?.preScreeningBilledTo
        : undefined,
      preScreening: ar?.contractBackgroundScreenRequirements || '',
      designatedPreScreening: !isNil(ar?.designatedPreScreeningProvider)
        ? ar?.designatedPreScreeningProvider
        : undefined,
      isOnboardingDocumentationRequired: !isNil(
        ar?.isOnboardingDocumentationRequired,
      )
        ? ar?.isOnboardingDocumentationRequired
        : undefined,
      specialInstructions: ar?.specialInstructions || '',
      onboardingDocumentation: ar?.onboardingDocumentationFileKeys || [],
      workPerformance: sar?.guaranteedWorkPerformance || '',
      timeEntry: sar?.timeEntry || '',
      assignmentNotes: sar?.timecardSpecialInstructions || '',
      breakInService: !isNil(sar?.breakInService)
        ? sar?.breakInService
        : undefined,
      isDrugScreensRequired: !isNil(ar?.isDrugScreensRequired)
        ? ar?.isDrugScreensRequired
        : undefined,
      drugScreenRequirements: ar?.drugScreenRequirements || '',
      isCredentialingRequired: !isNil(ar?.isCredentialingRequired)
        ? ar?.isCredentialingRequired
        : undefined,
      credentialingRequirements: ar?.credentialingRequirements || '',
      isSpecialRatesDefined: !isNil(ar?.isSpecialRatesDefined)
        ? ar?.isSpecialRatesDefined
        : undefined,
      specialRateRequirements: ar?.specialRateRequirements || '',
      cspNotes: ar?.cspNotes || '',
    },
  };
};
